/**
 * UsersPage - Admin page for user management
 * Feature: 001-user-role-management (Phase 4 - T067)
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useUser, useAuth } from '@clerk/clerk-react';
import { UserPlus, UserCog, AlertCircle } from 'lucide-react';
import { UsersTable } from '../../components/admin/UsersTable';
import { UserInviteModal } from '../../components/admin/UserInviteModal';
import { UserCreateModal } from '../../components/admin/UserCreateModal';
import { ConfirmationModal } from '../../components/admin/ConfirmationModal';
import { userService } from '../../services/userService';
import type {
  InviteUserRequest,
  UserOrInvitation,
  UserInvitation,
  User,
  CreateUserRequest,
  CreateUserResponse,
} from '../../types/user';

interface PendingAction {
  type: 'block' | 'unblock' | 'delete';
  userId: string;
  email: string;
}

export function UsersPage() {
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [syncStatusFilter, setSyncStatusFilter] = useState<string>('');

  const queryClient = useQueryClient();
  const { user: clerkUser } = useUser();
  const { getToken } = useAuth();

  // Fetch users list
  const {
    data: usersData,
    isLoading: isLoadingUsers,
    error: usersError,
  } = useQuery({
    queryKey: ['users', currentPage, pageSize, roleFilter, syncStatusFilter],
    queryFn: () => {
      if (!clerkUser) throw new Error('Not authenticated');
      return userService.listUsers(() => getToken(), {
        page: currentPage,
        size: pageSize,
        role: roleFilter || undefined,
        sync_status: syncStatusFilter === 'invited' ? undefined : (syncStatusFilter || undefined),
      });
    },
    enabled: !!clerkUser,
  });

  // Fetch invitations list
  const {
    data: invitations,
    isLoading: isLoadingInvitations,
    error: invitationsError,
  } = useQuery({
    queryKey: ['invitations'],
    queryFn: () => {
      if (!clerkUser) throw new Error('Not authenticated');
      return userService.getInvitations(() => getToken());
    },
    enabled: !!clerkUser,
  });

  // Combine users and invitations
  const combinedUsers: UserOrInvitation[] = [
    // Add existing users
    ...(usersData?.users || []).map((user: User): UserOrInvitation => ({
      ...user,
      sync_status: user.sync_status,
      is_blocked: user.is_blocked,  // From Clerk's banned status
    })),
    // Add invitations as "invited" users
    ...(invitations || []).map((inv: UserInvitation): UserOrInvitation => ({
      id: inv.id,
      email: inv.email,
      role: inv.role,
      sync_status: 'invited' as const,
      is_blocked: false,  // Invitations can't be blocked
      created_at: inv.invited_at,
      invited_at: inv.invited_at,
      expires_at: inv.expires_at,
      invited_by: inv.invited_by || undefined,
    })),
  ];

  // Filter combined list based on sync status
  const filteredUsers = syncStatusFilter
    ? combinedUsers.filter(user => user.sync_status === syncStatusFilter)
    : combinedUsers;

  const isLoading = isLoadingUsers || isLoadingInvitations;
  const error = usersError || invitationsError;

  // Invite user mutation
  const inviteUserMutation = useMutation({
    mutationFn: (data: InviteUserRequest) => {
      if (!clerkUser) throw new Error('Not authenticated');
      return userService.inviteUser(() => getToken(), data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
      setIsInviteModalOpen(false);
    },
  });

  const createUserMutation = useMutation({
    mutationFn: (data: CreateUserRequest): Promise<CreateUserResponse> => {
      if (!clerkUser) throw new Error('Not authenticated');
      return userService.createUser(() => getToken(), data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
    },
  });

  const handleCreateUser = async (data: CreateUserRequest): Promise<CreateUserResponse> => {
    return await createUserMutation.mutateAsync(data);
  };

  // Sync user mutation
  const syncUserMutation = useMutation({
    mutationFn: (userId: string) => {
      if (!clerkUser) throw new Error('Not authenticated');
      return userService.syncUser(() => getToken(), userId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  // Update user status mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ userId, status }: { userId: string; status: 'active' | 'blocked' }) => {
      if (!clerkUser) throw new Error('Not authenticated');
      return userService.updateUserStatus(() => getToken(), userId, { status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: (userId: string) => {
      if (!clerkUser) throw new Error('Not authenticated');
      return userService.deleteUser(() => getToken(), userId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
    },
  });

  const handleInviteUser = async (data: InviteUserRequest) => {
    await inviteUserMutation.mutateAsync(data);
  };

  const handleSyncUser = async (userId: string) => {
    await syncUserMutation.mutateAsync(userId);
  };

  // Show confirmation modal for status update
  const handleUpdateStatus = (userId: string, status: 'active' | 'blocked') => {
    const user = filteredUsers.find(u => u.id === userId);
    if (!user) return;

    // Check if user is trying to block themselves
    if (status === 'blocked' && user.clerk_id === clerkUser?.id) {
      alert('You cannot block your own account.');
      return;
    }

    setPendingAction({
      type: status === 'blocked' ? 'block' : 'unblock',
      userId,
      email: user.email,
    });
  };

  // Show confirmation modal for delete
  const handleDeleteUser = (userId: string, email: string) => {
    // Check if user is trying to delete themselves
    const user = filteredUsers.find(u => u.id === userId);
    if (user && user.clerk_id === clerkUser?.id) {
      alert('You cannot delete your own account.');
      return;
    }

    setPendingAction({
      type: 'delete',
      userId,
      email,
    });
  };

  // Execute the pending action
  const handleConfirmAction = async () => {
    if (!pendingAction) return;

    if (pendingAction.type === 'delete') {
      await deleteUserMutation.mutateAsync(pendingAction.userId);
    } else {
      const status = pendingAction.type === 'block' ? 'blocked' : 'active';
      await updateStatusMutation.mutateAsync({ userId: pendingAction.userId, status });
    }
    setPendingAction(null);
  };

  // Get confirmation modal content based on pending action
  const getConfirmationContent = () => {
    if (!pendingAction) return { title: '', message: '', confirmText: '' };

    switch (pendingAction.type) {
      case 'block':
        return {
          title: 'Block User',
          message: `Are you sure you want to block ${pendingAction.email}? They will not be able to access the application until unblocked.`,
          confirmText: 'Block User',
          variant: 'warning' as const,
        };
      case 'unblock':
        return {
          title: 'Unblock User',
          message: `Are you sure you want to unblock ${pendingAction.email}? They will regain access to the application.`,
          confirmText: 'Unblock User',
          variant: 'warning' as const,
        };
      case 'delete':
        return {
          title: 'Delete User',
          message: `Are you sure you want to permanently delete ${pendingAction.email}? This action cannot be undone and will remove the user from both the application and Clerk.`,
          confirmText: 'Delete User',
          variant: 'danger' as const,
        };
    }
  };

  return (
    <div className="min-h-screen bg-brand-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-brand-text">User Management</h1>
              <p className="mt-2 text-sm text-brand-muted">
                Manage users, roles, and permissions
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-brand-primary hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary"
              >
                <UserCog className="h-5 w-5 mr-2" />
                Create User
              </button>
              <button
                onClick={() => setIsInviteModalOpen(true)}
                className="inline-flex items-center px-4 py-2 border border-brand-border rounded-md shadow-sm text-sm font-medium text-brand-text bg-brand-bg hover:bg-brand-surface focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary"
              >
                <UserPlus className="h-5 w-5 mr-2" />
                Invite User
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 bg-brand-bg rounded-lg shadow p-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <label htmlFor="role-filter" className="block text-sm font-medium text-brand-text mb-1">
                Role
              </label>
              <select
                id="role-filter"
                value={roleFilter}
                onChange={(e) => {
                  setRoleFilter(e.target.value);
                  setCurrentPage(1); // Reset to first page
                }}
                className="block w-full px-3 py-2 border border-brand-border rounded-md shadow-sm focus:outline-none focus:ring-brand-primary focus:border-brand-primary sm:text-sm"
              >
                <option value="">All Roles</option>
                <option value="admin">Admin</option>
                <option value="user">User</option>
              </select>
            </div>
            <div className="flex-1">
              <label htmlFor="sync-status-filter" className="block text-sm font-medium text-brand-text mb-1">
                Sync Status
              </label>
              <select
                id="sync-status-filter"
                value={syncStatusFilter}
                onChange={(e) => {
                  setSyncStatusFilter(e.target.value);
                  setCurrentPage(1); // Reset to first page
                }}
                className="block w-full px-3 py-2 border border-brand-border rounded-md shadow-sm focus:outline-none focus:ring-brand-primary focus:border-brand-primary sm:text-sm"
              >
                <option value="">All Statuses</option>
                <option value="invited">Invited</option>
                <option value="synced">Synced</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
                <option value="manual">Manual</option>
              </select>
            </div>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="mb-6 rounded-md bg-brand-error p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-brand-error" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-brand-error">Error loading users</h3>
                <div className="mt-2 text-sm text-brand-error">
                  {error instanceof Error ? error.message : 'An unexpected error occurred'}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Users Table */}
        <div className="bg-brand-bg rounded-lg shadow">
          <UsersTable
            users={filteredUsers}
            onSyncUser={handleSyncUser}
            onUpdateStatus={handleUpdateStatus}
            onDeleteUser={handleDeleteUser}
            isLoading={isLoading}
          />
        </div>

        {/* Pagination */}
        {filteredUsers.length > 0 && (
          <div className="mt-6 flex items-center justify-between">
            <div className="text-sm text-brand-text">
              Showing <span className="font-medium">{filteredUsers.length}</span> user{filteredUsers.length !== 1 ? 's' : ''}
              {syncStatusFilter && <span> ({syncStatusFilter})</span>}
            </div>
          </div>
        )}

        {/* Invite User Modal */}
        <UserInviteModal
          isOpen={isInviteModalOpen}
          onClose={() => setIsInviteModalOpen(false)}
          onInvite={handleInviteUser}
        />

        <UserCreateModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onCreate={handleCreateUser}
        />

        {/* Confirmation Modal for block/unblock/delete */}
        <ConfirmationModal
          isOpen={!!pendingAction}
          onClose={() => setPendingAction(null)}
          onConfirm={handleConfirmAction}
          {...getConfirmationContent()}
        />
      </div>
    </div>
  );
}
